import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../infrastructure/utils/formatters";
import {
  getDerivedLoanFormValues,
  resolvePlantLoanPlan,
} from "../../../../infrastructure/utils/plantLoanPlans";
import PlantLoanSummaryCard from "../../../components/loan/PlantLoanSummaryCard";
import LoanGuaranteeChequeIntentSelector from "../../SLN/shared/customerRequest/LoanGuaranteeChequeIntentSelector";
import { resolveLoanGuaranteeChequeIntentLabel } from "../../SLN/shared/customerRequest/loanGuaranteeChequeIntentConstants";
import {
  ReviewDocumentStatus,
  ReviewFieldGrid,
  ReviewSectionCard,
} from "../../../components/shared/request-review/RequestReviewPanels";
import DropdownPicker from "../../../components/shared/dropdown";
import FileUploader from "../../../components/shared/uploader/FileUploader";
import {
  applicantGoalOptions,
  formatApplicantGoalsLabel,
  initialRegistrationConditions,
  locationUsageTypeOptions,
  ownershipStatusOptions,
  OWNERSHIP_REGISTRATION_CONDITION,
  platformAcquaintanceSourceOptions,
  powerPlantTypeOptions,
} from "../assistedRegistrationFormConstants";
import { ELECTRIC_BILL_FILE_ACCEPT, isApartmentInstallation } from "../../SLN/shared/customerRequest/customerRequestValidationSchemas";
import AssistedRegistrationLocationSection from "./AssistedRegistrationLocationSection";

const APARTMENT_DOCUMENTS = [
  {
    field: "boardConsentDocumentUrl",
    label: "رضایت‌نامه هیئت‌مدیره",
    download: "CustomerRequests/DownloadBoardResolution",
    fileName: "رضایت‌نامه هیئت‌مدیره.pdf",
    placeholder: "رضایت‌نامه هیئت‌مدیره را انتخاب کنید (اجباری)",
  },
  {
    field: "authorizedRepresentativeLetterUrl",
    label: "نامه معرفی نماینده",
    download: "CustomerRequests/DownloadRepresentativeIntroductionLetter",
    fileName: "نامه معرفی نماینده.pdf",
    placeholder: "نامه معرفی نماینده را انتخاب کنید (اجباری)",
  },
  {
    field: "agreementDocumentUrl",
    label: "تفاهم‌نامه همکاری",
    download: "CustomerRequests/DownloadAgreementTemplate",
    fileName: "تفاهم‌نامه همکاری.pdf",
    placeholder: "تفاهم‌نامه همکاری تکمیل شده را انتخاب کنید (اجباری)",
    accept: "application/pdf",
  },
];

const ApartmentDocumentUploads = ({
  baseUrl,
  onFileField,
  handleFileChange,
}) => (
  <>
    <Grid item xs={12}>
      <Card sx={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Typography variant="body2">
            برای پروژه‌های آپارتمانی، بارگذاری اسناد زیر الزامی است:
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    {APARTMENT_DOCUMENTS.map((doc) => (
      <Grid item xs={12} md={6} key={doc.field}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1 }}>
          <Typography variant="body2">
            {doc.label} <span style={{ color: "red" }}>*</span>
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                const response = await DefaultApiService.downloadFile(doc.download);
                const url = window.URL.createObjectURL(response);
                const link = document.createElement("a");
                link.href = url;
                link.download = doc.fileName;
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch {
                toast.error("خطا در دانلود فایل");
              }
            }}
            sx={{ fontSize: "0.7rem", px: 1, flexShrink: 0 }}
          >
            دانلود نمونه
          </Button>
        </Box>
        <FileUploader
          moduleName="SLN/CustomerData"
          maxFiles={1}
          label=""
          required
          baseUrl={baseUrl}
          accept={doc.accept}
          placeholder={doc.placeholder}
          onChange={handleFileChange(doc.field)}
          {...onFileField(doc.field)}
        />
      </Grid>
    ))}
  </>
);

function filterCitiesByProvince(cityList, provinceId) {
  if (!provinceId || !cityList?.length) return [];
  const provinceIdStr = String(provinceId);
  return cityList.filter((c) => String(c.provinceId) === provinceIdStr);
}

function resolveCitySelectValue(cityId, provinceId, cityList) {
  if (!cityId || !provinceId) return "";
  const cityIdStr = String(cityId);
  const filtered = filterCitiesByProvince(cityList, provinceId);
  return filtered.some((c) => String(c.value) === cityIdStr) ? cityIdStr : "";
}

const AssistedRegistrationRequestSteps = ({
  requestStep,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
  provinces,
  cities,
  plantCapacityOptions,
  baseUrl,
  onParseLocation,
  profileSummary,
}) => {
  const onField = (name) => ({
    name,
    value: values[name] ?? "",
    onChange: wrapFormikHandleChange(handleChange),
    onBlur: handleBlur,
    error: Boolean(errors[name] && touched[name]),
    helperText: touched[name] && errors[name] ? errors[name] : undefined,
  });

  const onFileField = (name) => ({
    name,
    value: values[name] ?? "",
    error: Boolean(errors[name] && touched[name]),
    helperText: touched[name] && errors[name] ? errors[name] : undefined,
  });

  const handleFileChange = (name) => (fileValue) => {
    setFieldValue(name, typeof fileValue === "string" ? fileValue : "");
    setFieldTouched(name, true, false);
  };

  if (requestStep === 0) {
    return (
      <Grid item xs={12}>
        <Box
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            bgcolor: "#fff",
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
            <InfoOutlinedIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              شرایط احداث نیروگاه خورشیدی
            </Typography>
          </Stack>
          <Box component="ol" sx={{ pr: 3, mb: 2 }}>
            {initialRegistrationConditions.map((c) => (
              <Box component="li" key={c} sx={{ mb: 1, lineHeight: 1.8 }}>
                {c}
              </Box>
            ))}
          </Box>
          <FormControl error={Boolean(errors.initialConditionsAccepted && touched.initialConditionsAccepted)}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(values.initialConditionsAccepted)}
                  onChange={(e) => setFieldValue("initialConditionsAccepted", e.target.checked)}
                />
              }
              label="شرایط احداث را مطالعه کرده و می‌پذیرم."
            />
            {errors.initialConditionsAccepted && touched.initialConditionsAccepted ? (
              <Typography variant="caption" color="error">
                {errors.initialConditionsAccepted}
              </Typography>
            ) : null}
          </FormControl>
        </Box>
      </Grid>
    );
  }

  if (requestStep === 1) {
    const loanPlan = values.powerPlantType
      ? resolvePlantLoanPlan(values.powerPlantType)
      : null;

    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">مشخصات نیروگاه و قبض برق</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="ظرفیت نیروگاه"
            list={plantCapacityOptions}
            optionName="name"
            optionValue="value"
            filterable
            {...onField("plantCapacity")}
            onChange={(e) => setFieldValue("plantCapacity", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="نوع نیروگاه"
            list={powerPlantTypeOptions}
            optionName="name"
            optionValue="value"
            {...onField("powerPlantType")}
            onChange={(e) => {
              setFieldValue("powerPlantType", e.target.value);
              if (values.needsLoan) {
                const derived = getDerivedLoanFormValues(e.target.value);
                Object.entries(derived).forEach(([k, v]) => setFieldValue(k, v));
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="کاربری محل نصب"
            list={locationUsageTypeOptions}
            optionName="name"
            optionValue="value"
            {...onField("locationUsageType")}
            onChange={(e) => setFieldValue("locationUsageType", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="مساحت قابل نصب (متر مربع)"
            type="number"
            {...onField("installableAreaSquareMeter")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="شناسه قبض برق (۱۳ رقم)"
            inputProps={{ maxLength: 13, inputMode: "numeric" }}
            {...onField("electricBoillNumber")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset">
            <FormLabel>نوع محل نصب</FormLabel>
            <RadioGroup
              row
              name="installationType"
              value={values.installationType || "Villa"}
              onChange={(e) => {
                const next = e.target.value;
                setFieldValue("installationType", next);
                if (!isApartmentInstallation(next)) {
                  setFieldValue("unitCount", "");
                  setFieldTouched("unitCount", false);
                  setFieldValue("boardConsentDocumentUrl", "");
                  setFieldValue("authorizedRepresentativeLetterUrl", "");
                  setFieldValue("agreementDocumentUrl", "");
                }
              }}
            >
              <FormControlLabel value="Villa" control={<Radio />} label="ویلایی" />
              <FormControlLabel value="Apartment" control={<Radio />} label="آپارتمانی" />
            </RadioGroup>
          </FormControl>
        </Grid>
        {isApartmentInstallation(values.installationType) ? (
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="تعداد واحد" type="number" {...onField("unitCount")} />
          </Grid>
        ) : null}
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="وضعیت مالکیت"
            list={ownershipStatusOptions}
            optionName="name"
            optionValue="value"
            {...onField("ownershipStatus")}
            onChange={(e) => setFieldValue("ownershipStatus", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">
            {OWNERSHIP_REGISTRATION_CONDITION}
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="نحوه آشنایی با سولارنت"
            list={platformAcquaintanceSourceOptions}
            optionName="name"
            optionValue="value"
            {...onField("platformAcquaintanceSource")}
            onChange={(e) => setFieldValue("platformAcquaintanceSource", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend">اهداف متقاضی</FormLabel>
          <FormGroup row>
            {applicantGoalOptions.map((goal) => (
              <FormControlLabel
                key={goal.value}
                control={
                  <Checkbox
                    checked={Boolean((Number(values.applicantGoalFlags) || 0) & goal.value)}
                    onChange={(e) => {
                      const current = Number(values.applicantGoalFlags) || 0;
                      setFieldValue(
                        "applicantGoalFlags",
                        e.target.checked ? current | goal.value : current & ~goal.value
                      );
                    }}
                  />
                }
                label={goal.label}
              />
            ))}
          </FormGroup>
          {errors.applicantGoalFlags && touched.applicantGoalFlags ? (
            <Typography variant="caption" color="error">
              {errors.applicantGoalFlags}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(values.needsLoan)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFieldValue("needsLoan", checked);
                  if (!checked) {
                    setFieldValue("loanGuaranteeChequeIntent", "");
                    setFieldValue("guarantorFirstName", "");
                    setFieldValue("guarantorLastName", "");
                    setFieldValue("guarantorNationalCode", "");
                    setFieldValue("guarantorMobile", "");
                  }
                  if (checked && values.powerPlantType) {
                    const derived = getDerivedLoanFormValues(values.powerPlantType);
                    Object.entries(derived).forEach(([k, v]) => setFieldValue(k, v));
                  }
                }}
              />
            }
            label="نیاز به وام دارم"
          />
          {values.needsLoan && loanPlan && !loanPlan.available ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              برای نیروگاه آفگرید در حال حاضر وام فعال نیست.
            </Alert>
          ) : null}
        </Grid>
        {values.needsLoan && loanPlan?.available ? (
          <Grid item xs={12}>
            <PlantLoanSummaryCard needsLoan={Boolean(values.needsLoan)} powerPlantType={values.powerPlantType} />
          </Grid>
        ) : null}
        {values.needsLoan && loanPlan?.available ? (
          <Grid item xs={12}>
            <LoanGuaranteeChequeIntentSelector
              value={values.loanGuaranteeChequeIntent}
              onChange={(next) => {
                setFieldValue("loanGuaranteeChequeIntent", next);
                setFieldTouched("loanGuaranteeChequeIntent", true, false);
                if (String(next) !== "2") {
                  setFieldValue("guarantorFirstName", "");
                  setFieldValue("guarantorLastName", "");
                  setFieldValue("guarantorNationalCode", "");
                  setFieldValue("guarantorMobile", "");
                }
              }}
              error={errors.loanGuaranteeChequeIntent}
              touched={touched.loanGuaranteeChequeIntent}
              formValues={values}
              formErrors={errors}
              formTouched={touched}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
            />
          </Grid>
        ) : null}
      </>
    );
  }

  if (requestStep === 2) {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">اطلاعات مکانی و آدرس</Typography>
        </Grid>
        <AssistedRegistrationLocationSection
          locationInput={values.locationInput}
          onLocationInputChange={(v) => setFieldValue("locationInput", v)}
          xPoint={values.xPoint}
          yPoint={values.yPoint}
          onCoordsChange={({ xPoint, yPoint, address }) => {
            setFieldValue("xPoint", xPoint);
            setFieldValue("yPoint", yPoint);
            if (address && !values.address) setFieldValue("address", address);
            setFieldTouched("xPoint", true);
            setFieldTouched("yPoint", true);
          }}
          onParseLocation={onParseLocation}
          locationError={touched.locationInput && errors.locationInput}
          mapError={touched.xPoint && errors.xPoint ? errors.xPoint : undefined}
        />
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="استان"
            list={provinces}
            optionName="label"
            optionValue="value"
            filterable
            value={values.provinceId}
            onChange={(e) => {
              setFieldValue("provinceId", String(e.target.value));
              setFieldValue("cityId", "");
            }}
            error={errors.provinceId && touched.provinceId}
            helperText={touched.provinceId && errors.provinceId}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownPicker
            label="شهرستان"
            list={filterCitiesByProvince(cities, values.provinceId)}
            optionName="label"
            optionValue="value"
            filterable
            disabled={!values.provinceId}
            value={resolveCitySelectValue(values.cityId, values.provinceId, cities)}
            onChange={(e) => setFieldValue("cityId", String(e.target.value))}
            error={errors.cityId && touched.cityId}
            helperText={touched.cityId && errors.cityId}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField fullWidth label="آدرس" {...onField("address")} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="کد پستی"
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
            {...onField("postalCode")}
          />
        </Grid>
        {isApartmentInstallation(values.installationType) ? (
          <ApartmentDocumentUploads
            baseUrl={baseUrl}
            onFileField={onFileField}
            handleFileChange={handleFileChange}
          />
        ) : null}
      </>
    );
  }

  if (requestStep === 3) {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">بارگذاری مدارک</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploader
            moduleName="SLN/CustomerData"
            maxFiles={1}
            label="تصویر یا PDF قبض برق"
            accept={ELECTRIC_BILL_FILE_ACCEPT}
            baseUrl={baseUrl}
            onChange={handleFileChange("electricBoillUrl")}
            {...onFileField("electricBoillUrl")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploader
            moduleName="SLN/CustomerData"
            maxFiles={1}
            label="تصویر روی کارت ملی"
            accept="image/*"
            baseUrl={baseUrl}
            onChange={handleFileChange("nationalCardUrl")}
            {...onFileField("nationalCardUrl")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploader
            moduleName="SLN/CustomerData"
            maxFiles={1}
            label="تصویر پشت کارت ملی"
            accept="image/*"
            baseUrl={baseUrl}
            onChange={handleFileChange("nationalCardBackUrl")}
            {...onFileField("nationalCardBackUrl")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploader
            moduleName="SLN/CustomerData"
            maxFiles={1}
            label="عکس اول محل نصب"
            accept="image/*"
            baseUrl={baseUrl}
            onChange={handleFileChange("plantInstallImage1Url")}
            {...onFileField("plantInstallImage1Url")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FileUploader
            moduleName="SLN/CustomerData"
            maxFiles={1}
            label="عکس دوم محل نصب"
            accept="image/*"
            baseUrl={baseUrl}
            onChange={handleFileChange("plantInstallImage2Url")}
            {...onFileField("plantInstallImage2Url")}
          />
        </Grid>
      </>
    );
  }

  if (requestStep === 4) {
    const capacityLabel =
      plantCapacityOptions.find((item) => String(item.value) === String(values.plantCapacity))
        ?.name || values.plantCapacity;
    const ownershipLabel =
      ownershipStatusOptions.find((item) => String(item.value) === String(values.ownershipStatus))
        ?.name || values.ownershipStatus;
    const acquaintanceLabel =
      platformAcquaintanceSourceOptions.find(
        (item) => String(item.value) === String(values.platformAcquaintanceSource)
      )?.name || values.platformAcquaintanceSource;

    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6">بررسی نهایی</Typography>
          <Typography variant="body2" color="text.secondary">
            قبل از ارسال، اطلاعات را مرور کنید.
          </Typography>
        </Grid>
        {profileSummary ? (
          <Grid item xs={12} md={6}>
            <ReviewSectionCard title="پروفایل متقاضی">
              <ReviewFieldGrid
                fields={[
                  { label: "نام", value: profileSummary.firstName },
                  { label: "نام خانوادگی", value: profileSummary.lastName },
                  { label: "کد ملی", value: profileSummary.nationalCode },
                  { label: "موبایل", value: profileSummary.mobile },
                ]}
              />
            </ReviewSectionCard>
          </Grid>
        ) : null}
        <Grid item xs={12} md={6}>
          <ReviewSectionCard title="مشخصات نیروگاه" icon={<SolarPowerIcon />}>
            <ReviewFieldGrid
              fields={[
                { label: "ظرفیت", value: capacityLabel ? `${capacityLabel}` : null },
                { label: "شناسه قبض", value: values.electricBoillNumber },
                { label: "وضعیت مالکیت", value: ownershipLabel },
                { label: "نحوه آشنایی", value: acquaintanceLabel },
                {
                  label: "اهداف متقاضی",
                  value: formatApplicantGoalsLabel(values.applicantGoalFlags),
                },
                { label: "وام", value: values.needsLoan ? "بله" : "خیر" },
                ...(values.needsLoan && values.loanGuaranteeChequeIntent
                  ? [
                      {
                        label: "وضعیت چک صیادی",
                        value: resolveLoanGuaranteeChequeIntentLabel(
                          values.loanGuaranteeChequeIntent
                        ),
                      },
                    ]
                  : []),
                ...(values.needsLoan &&
                String(values.loanGuaranteeChequeIntent) === "2"
                  ? [
                      {
                        label: "ضامن",
                        value: `${values.guarantorFirstName || ""} ${
                          values.guarantorLastName || ""
                        }`.trim(),
                      },
                      {
                        label: "کد ملی ضامن",
                        value: values.guarantorNationalCode || null,
                      },
                      {
                        label: "موبایل ضامن",
                        value: values.guarantorMobile || null,
                      },
                    ]
                  : []),
              ]}
            />
          </ReviewSectionCard>
        </Grid>
        <Grid item xs={12}>
          <ReviewSectionCard title="مدارک">
            <ReviewDocumentStatus
              label="قبض برق"
              uploaded={Boolean(values.electricBoillUrl)}
            />
            <ReviewDocumentStatus
              label="روی کارت ملی"
              uploaded={Boolean(values.nationalCardUrl)}
            />
            <ReviewDocumentStatus
              label="پشت کارت ملی"
              uploaded={Boolean(values.nationalCardBackUrl)}
            />
            <ReviewDocumentStatus
              label="عکس محل نصب ۱"
              uploaded={Boolean(values.plantInstallImage1Url)}
            />
            <ReviewDocumentStatus
              label="عکس محل نصب ۲"
              uploaded={Boolean(values.plantInstallImage2Url)}
            />
            {isApartmentInstallation(values.installationType) ? (
              <>
                <ReviewDocumentStatus
                  label="رضایت‌نامه هیئت‌مدیره"
                  uploaded={Boolean(values.boardConsentDocumentUrl)}
                />
                <ReviewDocumentStatus
                  label="نامه معرفی نماینده"
                  uploaded={Boolean(values.authorizedRepresentativeLetterUrl)}
                />
                <ReviewDocumentStatus
                  label="تفاهم‌نامه همکاری"
                  uploaded={Boolean(values.agreementDocumentUrl)}
                />
              </>
            ) : null}
          </ReviewSectionCard>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="توضیحات (اختیاری)"
            {...onField("note")}
          />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="warning">
            با ارسال پرونده، امکان ویرایش اطلاعات توسط نماینده از بین می‌رود.
          </Alert>
        </Grid>
      </>
    );
  }

  return null;
};

export default AssistedRegistrationRequestSteps;
